import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter, Search, Download, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TalentGraph() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    filterPlayers();
  }, [players, searchQuery, roleFilter]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/players?limit=100`, {
        timeout: 5000
      });
      setPlayers(response.data);
      setFilteredPlayers(response.data);
    } catch (error) {
      toast.error('Failed to fetch players');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPlayers = () => {
    let filtered = players;
    
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (roleFilter !== 'All') {
      filtered = filtered.filter(p => p.role === roleFilter);
    }
    
    setFilteredPlayers(filtered);
  };

  const toggleWatchlist = (playerId) => {
    if (watchlist.includes(playerId)) {
      setWatchlist(watchlist.filter(id => id !== playerId));
      toast.success('Removed from watchlist');
    } else {
      setWatchlist([...watchlist, playerId]);
      toast.success('Added to watchlist');
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Header */}
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <div className="flex items-center justify-between">
            <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="page-title">Talent Graph & Scouting</h1>
            <Button 
              variant="outline" 
              className="text-green-400 border-green-400"
              data-testid="export-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Watchlist
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
        {/* Filters */}
        <Card className="glass-card mb-8" data-testid="filters-card">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 p-2 rounded-lg bg-white/5">
                <Search className="w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search players..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none text-white"
                  data-testid="player-search-input"
                />
              </div>
            </div>
            
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Roles</SelectItem>
                <SelectItem value="Batsman">Batsman</SelectItem>
                <SelectItem value="Bowler">Bowler</SelectItem>
                <SelectItem value="All-Rounder">All-Rounder</SelectItem>
                <SelectItem value="Wicket-Keeper">Wicket-Keeper</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Players Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPlayers.map((player) => (
            <Card 
              key={player.id} 
              className="glass-card cursor-pointer group relative"
              data-testid={`player-card-${player.id}`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleWatchlist(player.id);
                }}
                className="absolute top-4 right-4 z-10"
                data-testid={`watchlist-button-${player.id}`}
              >
                <Heart 
                  className={`w-6 h-6 transition-colors ${
                    watchlist.includes(player.id) 
                      ? 'fill-red-500 text-red-500' 
                      : 'text-gray-400 hover:text-red-400'
                  }`} 
                />
              </button>

              <div 
                onClick={() => navigate(`/players/${player.id}`)}
                className="text-center space-y-4"
              >
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-2xl font-bold">
                  {player.name.split(' ').map(n => n[0]).join('')}
                </div>
                
                <div>
                  <h3 className="font-bold text-lg mb-1" data-testid={`player-name-${player.id}`}>{player.name}</h3>
                  <p className="text-sm text-gray-400">{player.role}</p>
                </div>
                
                {player.talent_score && (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="text-2xl font-bold gradient-text" data-testid={`talent-score-${player.id}`}>
                      {player.talent_score}
                    </div>
                    <span className="text-xs text-gray-400">Talent</span>
                  </div>
                )}
                
                {player.youth_potential && player.age && player.age < 19 && (
                  <div className="flex items-center justify-center space-x-2 mt-2">
                    <div className="text-lg font-bold text-yellow-400" data-testid={`youth-potential-${player.id}`}>
                      {player.youth_potential}
                    </div>
                    <span className="text-xs text-yellow-500">Youth Potential ⭐</span>
                  </div>
                )}
                
                <div className="text-xs text-gray-500">
                  Age {player.age} • {player.country}
                </div>
                
                {player.stats && (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {player.stats.matches && (
                      <div className="p-2 rounded bg-white/5">
                        <div className="font-bold text-green-400">{player.stats.matches}</div>
                        <div className="text-gray-500">Matches</div>
                      </div>
                    )}
                    {player.stats.runs && (
                      <div className="p-2 rounded bg-white/5">
                        <div className="font-bold text-green-400">{player.stats.runs}</div>
                        <div className="text-gray-500">Runs</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
        
        {filteredPlayers.length === 0 && (
          <div className="text-center py-16">
            <p className={`text-gray-400 text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} data-testid="no-results">No players found</p>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}